/**
 * SSD Store Slice
 *
 * Zustand slice for SSD artifact management state.
 * Domain: SessionStorageDownloads > ArtifactManagement
 *
 * Holds artifact list, filters, and download states for the My Files page.
 * Integrates with SSD API for artifact listing and download URL generation.
 */

import { clearError as clearOpError, isOperationLoading, setError, setLoading } from '@gw/ui';

import { ssdApi } from '@/api/ssd';
import type {
  ArtifactItem,
  ListArtifactsParams,
  ArtifactType,
  ArtifactDownloadUrlResponse,
} from '@/types/ssd.types';

import type { ErrorStates, LoadingStates } from '@gw/ui/types';
import type { StateCreator } from 'zustand';

// ============================================================================
// Slice Interface
// ============================================================================

export interface ArtifactFilters {
  type?: ArtifactType;
  search?: string;
  limit?: number;
}

export interface SsdSlice {
  // State properties
  artifacts: ArtifactItem[];
  artifactsIsLoading: LoadingStates;
  artifactsErrors: ErrorStates;
  artifactFilters: ArtifactFilters;
  downloadUrls: Record<string, ArtifactDownloadUrlResponse>;
  deletingIds: Record<string, boolean>;

  // Artifact actions
  fetchArtifacts: (params?: ListArtifactsParams) => Promise<void>;
  getDownloadUrl: (artifactId: string) => Promise<string>;
  deleteArtifact: (artifactId: string) => Promise<void>;

  // Filter actions
  setTypeFilter: (type: ArtifactType | null) => void;
  setSearchFilter: (search: string) => void;
  setLimitFilter: (limit: number) => void;

  // Utility actions
  clearArtifactsError: (operation?: string) => void;
  resetArtifacts: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialSsdState = {
  artifacts: [],
  artifactsIsLoading: {},
  artifactsErrors: {},
  artifactFilters: {},
  downloadUrls: {},
  deletingIds: {},
};

// ============================================================================
// Slice Creator
// ============================================================================

export const createSsdSlice: StateCreator<SsdSlice, [], [], SsdSlice> = (set, get) => ({
  ...initialSsdState,

  // ============================================================================
  // Artifact Actions
  // ============================================================================

  /**
   * Fetch ALL artifacts for the current user, following nextToken until
   * exhausted. The My Files TreeView groups by type then source entity
   * across the whole set — a single page would silently hide any
   * type/source folder whose artifacts didn't land on page one (GW-10795).
   */
  fetchArtifacts: async (params?: ListArtifactsParams) => {
    const operation = 'fetchArtifacts';
    const currentState = get();

    if (isOperationLoading(currentState.artifactsIsLoading, operation)) return;

    set({
      artifactsIsLoading: setLoading(currentState.artifactsIsLoading, operation, true),
      artifactsErrors: clearOpError(currentState.artifactsErrors, operation),
    });

    try {
      let allItems: ArtifactItem[] = [];
      let nextToken: string | undefined;
      do {
        const response = await ssdApi.listArtifacts({
          ...params,
          ...(nextToken && { nextToken }),
        });
        allItems = allItems.concat(response.items ?? []);
        nextToken = response.nextToken;
      } while (nextToken);

      const state = get();
      set({
        artifacts: allItems,
        artifactsIsLoading: setLoading(state.artifactsIsLoading, operation, false),
      });
    } catch (error) {
      const state = get();
      set({
        artifactsIsLoading: setLoading(state.artifactsIsLoading, operation, false),
        artifactsErrors: setError(
          state.artifactsErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to fetch artifacts'
        ),
      });
    }
  },

  /**
   * Get download URL for an artifact
   */
  getDownloadUrl: async (artifactId: string) => {
    const operation = 'downloadUrl';
    const currentState = get();

    // Return cached URL if available and not expired
    const cached = currentState.downloadUrls[artifactId];
    if (cached && cached.expiresAt > Date.now() / 1000) {
      return cached.downloadUrl;
    }

    if (isOperationLoading(currentState.artifactsIsLoading, `${operation}-${artifactId}`)) {
      throw new Error('Download URL request already in progress');
    }

    set({
      artifactsIsLoading: setLoading(
        currentState.artifactsIsLoading,
        `${operation}-${artifactId}`,
        true
      ),
      artifactsErrors: clearOpError(currentState.artifactsErrors, operation),
    });

    try {
      const response = await ssdApi.getDownloadUrl(artifactId);

      const state = get();
      set({
        downloadUrls: {
          ...state.downloadUrls,
          [artifactId]: response,
        },
        artifactsIsLoading: setLoading(
          state.artifactsIsLoading,
          `${operation}-${artifactId}`,
          false
        ),
      });

      return response.downloadUrl;
    } catch (error) {
      const state = get();
      set({
        artifactsIsLoading: setLoading(
          state.artifactsIsLoading,
          `${operation}-${artifactId}`,
          false
        ),
        artifactsErrors: setError(
          state.artifactsErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to get download URL'
        ),
      });
      throw error;
    }
  },

  /**
   * Delete an artifact
   */
  deleteArtifact: async (artifactId: string) => {
    const currentState = get();

    // Don't delete if already in progress
    if (currentState.deletingIds[artifactId]) return;

    // Set loading state
    set({
      deletingIds: {
        ...currentState.deletingIds,
        [artifactId]: true,
      },
    });

    try {
      await ssdApi.deleteArtifact(artifactId);

      // On success: remove artifact from list and clear loading state
      const state = get();
      const { [artifactId]: _, ...remainingDeletingIds } = state.deletingIds;
      set({
        artifacts: state.artifacts.filter((artifact) => artifact.artifactId !== artifactId),
        deletingIds: remainingDeletingIds,
      });
    } catch (error) {
      // On error: keep artifact in list but clear loading state
      const state = get();
      const { [artifactId]: _, ...remainingDeletingIds } = state.deletingIds;
      set({
        deletingIds: remainingDeletingIds,
      });
      throw error;
    }
  },

  // ============================================================================
  // Filter Actions
  // ============================================================================

  /**
   * Set artifact type filter
   */
  setTypeFilter: (type: ArtifactType | null) => {
    const currentState = get();
    const newFilters = { ...currentState.artifactFilters };

    if (type === null) {
      delete newFilters.type;
    } else {
      newFilters.type = type;
    }

    set({ artifactFilters: newFilters });
  },

  /**
   * Set search filter
   */
  setSearchFilter: (search: string) => {
    const currentState = get();
    const newFilters = { ...currentState.artifactFilters };

    if (search.trim() === '') {
      delete newFilters.search;
    } else {
      newFilters.search = search.trim();
    }

    set({ artifactFilters: newFilters });
  },

  /**
   * Set pagination limit filter
   */
  setLimitFilter: (limit: number) => {
    const currentState = get();
    set({
      artifactFilters: {
        ...currentState.artifactFilters,
        limit,
      },
    });
  },

  // ============================================================================
  // Utility Actions
  // ============================================================================

  /**
   * Clear artifacts error for a specific operation
   */
  clearArtifactsError: (operation?: string) => {
    const currentState = get();

    if (operation) {
      set({
        artifactsErrors: clearOpError(currentState.artifactsErrors, operation),
      });
    } else {
      set({ artifactsErrors: {} });
    }
  },

  /**
   * Reset artifacts state to initial values
   */
  resetArtifacts: () => {
    set(initialSsdState);
  },
});
